import React from "react";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import computeAdsObjectiveCompletion from "./adsObjectiveCompletion";
import FastAPI from "../../../api/FastAPI";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import MultiToggle from "../../multiToggle/MultiToggle";
import DateSelector from "../dateSelector/DateSelector";
import ObjectiveComponent from "../ObjectiveComponent";

import type { JSX } from "react";

import type { Schedule } from "../../../api/FastAPI.types";
import type { CalendarView } from "../../../types/fast";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

interface DateSelectorLabelProps {
  currentDate: Date;
  isMonthView: boolean;
}

function DateSelectorLabel({
  currentDate,
  isMonthView,
}: DateSelectorLabelProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const label = isMonthView
    ? moment(currentDate).format("MMMM")
    : `${t("week")} ${moment(currentDate).format("W")}`;

  const subLabel = isMonthView
    ? moment(currentDate).format("YYYY")
    : `${moment(currentDate).startOf("week").format("DD/MM")} - ${moment(currentDate).endOf("week").format("DD/MM")}`;

  return (
    <React.Fragment>
      <div>{label}</div>
      <div>{subLabel}</div>
    </React.Fragment>
  );
}

interface CalendarOverviewProps {
  currentDate: Date;
  handleChangeDate: (date: Date) => void;
  onChangeView: (view: CalendarView) => void;
  currentView: CalendarView;
  isLoading: boolean;
  scheduleSelected?: Schedule;
}

function CalendarOverview({
  currentDate,
  handleChangeDate,
  onChangeView,
  currentView,
  isLoading,
  scheduleSelected,
}: CalendarOverviewProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { channelId } = useParams<FASTChannelRoutesParams>();

  const daysInMonth = moment(currentDate).daysInMonth();

  const formatedMonth = moment(currentDate).format("YYYY-MM");
  const firstDayOfWeek = moment(currentDate)
    .startOf("week")
    .format(API_DATE_FORMAT);

  const isMonthView = currentView === "month";

  const getScheduleKpisQuery = FastAPI.generateQuery({
    fn: FastAPI.getScheduleKpis,
    params: [
      {
        channelId,
        scheduleId: scheduleSelected?.uuid ?? "",
        month: isMonthView ? formatedMonth : undefined,
        week: isMonthView ? undefined : firstDayOfWeek,
      },
    ],
  });

  const { data: channelKpis } = useQuery({
    ...getScheduleKpisQuery,
    enabled: Boolean(channelId && scheduleSelected?.uuid),
    keepPreviousData: true,
  });

  const numberOfDays = isMonthView ? daysInMonth : 7;

  const ads = {
    duration: channelKpis?.data.ads_duration,
    completion: computeAdsObjectiveCompletion(channelKpis),
    objective: (scheduleSelected?.daily_ads_objective ?? 0) * numberOfDays,
  };

  const program = {
    duration: channelKpis?.data.programs_duration,
    completion: [
      {
        value: channelKpis?.data.programs_completion ?? 0,
      },
    ],
    objective: (scheduleSelected?.daily_programs_objective ?? 0) * numberOfDays,
  };

  const handleClickNext = (): void => {
    handleChangeDate(
      moment(currentDate)
        .add(1, isMonthView ? "month" : "week")
        .toDate(),
    );
  };
  const handleClickPrevious = (): void => {
    handleChangeDate(
      moment(currentDate)
        .add(-1, isMonthView ? "month" : "week")
        .toDate(),
    );
  };

  const calendarViewOptions: { value: CalendarView; label: string }[] = [
    { value: "month", label: t("monthly") },
    { value: "week", label: t("weekly") },
  ] as const;

  return (
    <div className="flex items-center">
      <ObjectiveComponent
        borderColor={scheduleSelected?.color}
        completion={program?.completion}
        current={program?.duration}
        objective={program?.objective}
        subTitle={scheduleSelected?.name}
        unit={isMonthView ? "month" : "week"}
        title={
          isMonthView
            ? t("monthly_program_objective")
            : t("weekly_program_objective")
        }
      />
      <DateSelector
        currentDay={currentDate}
        onClickCurrent={(): void => handleChangeDate(new Date())}
        onClickNext={handleClickNext}
        onClickPrevious={handleClickPrevious}
        label={
          <DateSelectorLabel
            currentDate={currentDate}
            isMonthView={isMonthView}
          />
        }
      >
        <MultiToggle
          isLoading={isLoading}
          onChange={onChangeView}
          options={calendarViewOptions}
          value={currentView}
        />
      </DateSelector>
      <ObjectiveComponent
        borderColor={scheduleSelected?.color}
        completion={ads.completion}
        current={ads?.duration}
        objective={ads?.objective}
        subTitle={scheduleSelected?.name}
        unit={isMonthView ? "month" : "week"}
        title={
          isMonthView ? t("monthly_ads_objective") : t("weekly_ads_objective")
        }
      />
    </div>
  );
}

export default CalendarOverview;
